import React, { useState, useRef, useEffect, useCallback } from 'react';
import { isCameraSupported, getCameraConstraints, compressImage } from '../utils/cameraUtils';
import '../styles/CameraCapture.css';
import ViewOnceToggle from './ViewOnceToggle';

function CameraCapture({ isVisible, onCapture, onClose }) {
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [viewOnce, setViewOnce] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back camera
  const [hasStream, setHasStream] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null); // Use ref to avoid stale closure issues

  // Stop camera helper using ref (no stale closure)
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setHasStream(false);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async (mode) => {
    try {
      setError(null);
      
      // Always stop existing stream first
      stopCamera();
      
      if (!isCameraSupported()) {
        throw new Error('Camera access is not supported in this browser');
      }

      const constraints = getCameraConstraints(mode, 'medium');
      let mediaStream;
      
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        // Fallback: if facingMode constraint fails (common on some devices),
        // try with just { facingMode: mode } without resolution constraints
        if (mode === 'environment') {
          console.warn('Back camera with resolution constraints failed, trying fallback...');
          try {
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { exact: 'environment' } },
              audio: false
            });
          } catch (err2) {
            // Final fallback: enumerate devices and pick back camera by deviceId
            console.warn('facingMode exact constraint failed, trying device enumeration...');
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cameras = devices.filter(d => d.kind === 'videoinput');
            // Usually the second camera is the back camera
            const backCamera = cameras.find(c => 
              c.label.toLowerCase().includes('back') || 
              c.label.toLowerCase().includes('rear') ||
              c.label.toLowerCase().includes('environment')
            ) || (cameras.length > 1 ? cameras[cameras.length - 1] : null);
            
            if (backCamera) {
              mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: backCamera.deviceId } },
                audio: false
              });
            } else {
              throw err; // Re-throw original error if no back camera found
            }
          }
        } else {
          // For front camera fallback, try without resolution constraints
          try {
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: 'user' },
              audio: false
            });
          } catch (err3) {
            // Last resort: any camera
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false
            });
          }
        }
      }
      
      streamRef.current = mediaStream;
      setHasStream(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      let errorMessage = 'Failed to access camera. ';
      
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera access and try again.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotSupportedError' || err.name === 'OverconstrainedError') {
        errorMessage += 'Camera is not supported with the requested settings.';
      } else {
        errorMessage += err.message || 'Unknown error occurred.';
      }
      
      setError(errorMessage);
    }
  }, [stopCamera]);

  // Start camera when component becomes visible or facingMode changes
  useEffect(() => {
    if (isVisible) {
      startCamera(facingMode);
    } else {
      stopCamera();
      setCapturedPhoto(null);
      setError(null);
    }
    
    return () => {
      stopCamera();
    };
  }, [isVisible, facingMode, startCamera, stopCamera]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedPhoto({
          blob: blob,
          url: URL.createObjectURL(blob)
        });
      }
      setIsCapturing(false);
    }, 'image/jpeg', 0.9);
  };

  const retakePhoto = () => {
    if (capturedPhoto) {
      URL.revokeObjectURL(capturedPhoto.url);
      setCapturedPhoto(null);
    }
  };

  const sendPhoto = async () => {
    if (capturedPhoto && onCapture) {
      try {
        // Create a File object from the blob
        let file = new File([capturedPhoto.blob], `camera-photo-${Date.now()}.jpg`, {
          type: 'image/jpeg'
        });
        
        // Compress the image if it's too large (> 2MB)
        if (file.size > 2 * 1024 * 1024) {
          file = await compressImage(file, 1280, 720, 0.7);
        }
        
        onCapture([file], { viewOnce });
        
        // Clean up
        URL.revokeObjectURL(capturedPhoto.url);
        setCapturedPhoto(null);
        onClose();
      } catch (error) {
        console.error('Error processing captured photo:', error);
        setError('Failed to process the captured photo. Please try again.');
      }
    }
  };

  const switchCamera = () => {
    setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
  };

  const handleClose = () => {
    if (capturedPhoto) {
      URL.revokeObjectURL(capturedPhoto.url);
      setCapturedPhoto(null);
    }
    onClose();
  };

  // Handle keyboard events for better UX
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isVisible) return;
      
      if (event.key === 'Escape') {
        handleClose();
      } else if (event.key === ' ' || event.key === 'Enter') {
        if (capturedPhoto) {
          sendPhoto();
        } else if (!error && hasStream) {
          event.preventDefault();
          capturePhoto();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, capturedPhoto, error, hasStream]);

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <div className="camera-capture-overlay">
      <div className="camera-capture-container">
        {/* Header */}
        <div className="camera-header">
          <button className="camera-close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <h3>Camera</h3>
          <button className="camera-switch-btn" onClick={switchCamera}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
              <circle cx="12" cy="13" r="4"/>
              <path d="M7 13a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H8a1 1 0 01-1-1v-2z" transform="rotate(180 12 13)"/>
            </svg>
          </button>
        </div>

        {/* Camera Content */}
        <div className="camera-content">
          {error ? (
            <div className="camera-error">
              <p>{error}</p>
              <button onClick={startCamera} className="retry-btn">
                Try Again
              </button>
            </div>
          ) : capturedPhoto ? (
            <div className="photo-preview">
              <img src={capturedPhoto.url} alt="Captured" />
            </div>
          ) : (
            <div className="camera-viewport">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              {isCapturing && (
                <div className="capture-overlay">
                  <div className="capture-flash"></div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="camera-controls">
          {capturedPhoto ? (
            <div className="photo-controls">
              <ViewOnceToggle enabled={viewOnce} onChange={setViewOnce} />
              <button className="retake-btn" onClick={retakePhoto}>
                Retake
              </button>
              <button className="send-btn" onClick={sendPhoto}>
                Send Photo
              </button>
            </div>
          ) : !error && (
            <div className="capture-controls">
              <button 
                className="capture-btn" 
                onClick={capturePhoto}
                disabled={isCapturing || !hasStream}
              >
                <div className="capture-btn-inner">
                  {isCapturing ? (
                    <div className="capture-spinner"></div>
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  )}
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Hidden canvas for photo capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}

export default CameraCapture;
